import React, { useState, useEffect, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import styles from "../styles/DiscussionPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faLock,
  faLockOpen,
} from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import {
  getDiscussionById,
  getDiscussionComments,
  updateDiscussionStatus,
} from "../api/api";
import { formatDate, getDifferenceInSeconds } from "../helpers";
import { AuthContext } from "../AuthProvider";
import { createComment, updateComment } from "../api/api";
import { useSnackbar } from "notistack";
import { OrbitProgress } from "react-loading-indicators";
import UserLink from "../components/UserLink";

const DiscussionPage = () => {
  const { keycloak, authenticated } = useContext(AuthContext);
  const { discussionId } = useParams();
  const [discussion, setDiscussion] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [isResponding, setIsResponding] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isClosingDiscussion, setIsClosingDiscussion] = useState(false);

  useEffect(() => {
    if (authenticated && keycloak?.tokenParsed) {
      // Check if user is admin based on roles
      const roles = keycloak.tokenParsed.realm_access?.roles || [];
      setIsUserAdmin(roles.includes("admin"));
    }
  }, [authenticated, keycloak]);

  useEffect(() => {
    const fetchDiscussion = async () => {
      try {
        const foundDiscussion = await getDiscussionById(discussionId);
        setDiscussion(foundDiscussion);
        const foundDiscussionComments = await getDiscussionComments(
          discussionId
        );
        setResponses(foundDiscussionComments);
      } catch (error) {
        console.error("Error fetching discussion:", error);
      }
    };
    fetchDiscussion();
  }, [discussionId]);

  const [responses, setResponses] = useState([]);
  const [newResponse, setNewResponse] = useState("");
  const [activeTab, setActiveTab] = useState("Edit");

  const [editingId, setEditingId] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [editTab, setEditTab] = useState("Edit"); // Tracks whether Edit or Preview is active

  // Function to start editing a response
  const handleEdit = (id, content) => {
    setEditingId(id);
    setEditedContent(content);
  };

  // Function to save the edited response
  const handleSaveEdit = async (id) => {
    if (editedContent.trim() === "") return;
    setIsResponding(true);
    try {
      const serverResponse = await updateComment(keycloak.token, {
        id: id,
        content: editedContent,
      });

      setResponses(
        responses.map((response) =>
          response.id === id
            ? {
                ...response,
                content: editedContent,
                updated_at: serverResponse.updated_at,
              }
            : response
        )
      );
      setEditingId(null);
      setEditedContent("");
    } catch (error) {
      console.error("Error updating comment:", error);
      enqueueSnackbar("Error updating comment!", {
        variant: "error",
      });
    } finally {
      setIsResponding(false);
    }
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedContent("");
  };

  const handleSendResponse = async () => {
    if (newResponse.trim() === "" || !authenticated || discussion.closed)
      return;

    setIsResponding(true);
    try {
      const serverResponse = await createComment(keycloak.token, {
        discussion_id: discussionId,
        content: newResponse,
      });
      setResponses([...responses, serverResponse]);
      setNewResponse(""); // Clear the input field
    } catch (error) {
      console.error("Error replying:", error);

      // Check for closed discussion error
      if (
        error.response &&
        error.response.data &&
        error.response.data.error === "Cannot comment on a closed discussion"
      ) {
        enqueueSnackbar(
          "This discussion is closed and no longer accepts comments.",
          {
            variant: "warning",
          }
        );
        // Update local discussion state to show it's closed
        setDiscussion({
          ...discussion,
          closed: true,
        });
      } else {
        enqueueSnackbar("Error replying!", {
          variant: "error",
        });
      }
    } finally {
      setIsResponding(false);
    }
  };

  // Function to toggle discussion closed status
  const handleToggleDiscussionStatus = async () => {
    if (!authenticated) return;

    setIsClosingDiscussion(true);
    try {
      const updatedDiscussion = await updateDiscussionStatus(
        keycloak.token,
        discussionId,
        !discussion.closed
      );

      setDiscussion({
        ...discussion,
        closed: updatedDiscussion.closed,
      });

      enqueueSnackbar(
        `Discussion ${
          updatedDiscussion.closed ? "closed" : "reopened"
        } successfully`,
        {
          variant: "success",
        }
      );
    } catch (error) {
      console.error("Error updating discussion status:", error);
      enqueueSnackbar(
        `Error ${discussion.closed ? "reopening" : "closing"} discussion`,
        {
          variant: "error",
        }
      );
    } finally {
      setIsClosingDiscussion(false);
    }
  };

  if (!discussion || responses.length === 0)
    return (
      <div className={styles.discussionPageLoading}>
        <OrbitProgress
          color="#cb95fa"
          size="large"
          text="Loading"
          textColor=""
        />
      </div>
    );

  return (
    <div className={styles.discussionPage}>
      {/* Header */}
      <div className={styles.header}>
        <FontAwesomeIcon icon={faComments} className={styles.headerIcon} />
        <div className={styles.headerText}>
          <h1 className={styles.title}>{discussion.title}</h1>
          <p className={styles.subtitle}>
            by{" "}
            <UserLink
              userId={discussion.user_id}
              userName={discussion.created_by}
            />{" "}
            - opened {formatDate(discussion.created_at)}
            {discussion.closed && (
              <span className={styles.closedTag}> • Closed</span>
            )}
          </p>
        </div>
        {authenticated &&
          (isUserAdmin || discussion.user_id === keycloak?.tokenParsed.sub) && (
            <button
              className={`${styles.toggleStatusButton} ${
                discussion.closed ? styles.reopenButton : styles.closeButton
              }`}
              onClick={handleToggleDiscussionStatus}
              disabled={isClosingDiscussion}
            >
              <FontAwesomeIcon icon={discussion.closed ? faLockOpen : faLock} />
              {isClosingDiscussion
                ? "Processing..."
                : discussion.closed
                ? "Reopen"
                : "Close"}
            </button>
          )}
      </div>

      {/* Responses */}
      <div className={styles.responses}>
        {responses.map((response) => (
          <div key={response.id} className={styles.responseBox}>
            {/* Response Header */}
            <div className={styles.responseHeader}>
              <div className={styles.userInfo}>
                <span className={styles.userName}>
                  {" "}
                  <UserLink
                    userId={response.user_id}
                    userName={response.created_by}
                  />
                </span>
                <span className={styles.postTime}>
                  {formatDate(response.created_at)}
                  {getDifferenceInSeconds(
                    response.created_at,
                    response.updated_at
                  ) > 1 && ` | edited ${formatDate(response.updated_at)}`}
                </span>
              </div>
              {!discussion.closed &&
                authenticated &&
                response.user_id === keycloak?.tokenParsed.sub && (
                  <button
                    className={styles.editButton}
                    onClick={() => handleEdit(response.id, response.content)}
                  >
                    Edit
                  </button>
                )}
            </div>

            {/* Response Content */}
            <div className={styles.responseContent}>
              {editingId === response.id ? (
                <div className={styles.editContainer}>
                  {/* Edit / Preview Tabs */}
                  <div className={styles.tabs}>
                    <button
                      className={`${styles.tabButton} ${
                        editTab === "Edit" ? styles.activeTab : ""
                      }`}
                      onClick={() => setEditTab("Edit")}
                    >
                      Edit
                    </button>
                    <button
                      className={`${styles.tabButton} ${
                        editTab === "Preview" ? styles.activeTab : ""
                      }`}
                      onClick={() => setEditTab("Preview")}
                    >
                      Preview
                    </button>
                  </div>

                  {/* Edit Area / Preview Area */}
                  <div className={styles.tabContent}>
                    {editTab === "Edit" ? (
                      <textarea
                        className={styles.editTextarea}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                      />
                    ) : (
                      <div className={styles.preview}>
                        <ReactMarkdown>
                          {editedContent || "Nothing to preview yet."}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {/* Save / Cancel Buttons */}
                  <div className={styles.editActions}>
                    <button
                      className={styles.cancelEditButton}
                      disabled={isResponding}
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                    <button
                      className={styles.saveEditButton}
                      disabled={isResponding}
                      onClick={() => handleSaveEdit(response.id)}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <ReactMarkdown>{response.content}</ReactMarkdown>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Response Section */}
      <div className={styles.addResponse}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tabButton} ${
              activeTab === "Edit" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("Edit")}
          >
            Edit
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === "Preview" ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab("Preview")}
          >
            Preview
          </button>
        </div>

        {/* Content Based on Active Tab */}
        <div className={styles.tabContent}>
          {activeTab === "Edit" ? (
            <textarea
              disabled={discussion.closed || !authenticated}
              className={styles.textarea}
              placeholder="Write your response here..."
              value={newResponse}
              onChange={(e) => setNewResponse(e.target.value)}
            />
          ) : (
            <div className={styles.preview}>
              <ReactMarkdown>
                {newResponse || "Nothing to preview yet."}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Send Button */}
        <div className={styles.sendContainer}>
          <button
            className={styles.sendButton}
            onClick={handleSendResponse}
            disabled={
              !newResponse.trim() ||
              discussion.closed ||
              !authenticated ||
              isResponding
            }
          >
            Send Response
          </button>
          {!authenticated && (
            <div className={styles.authLinks}>
              <Link
                className={styles.authLink}
                onClick={() => keycloak.register()}
              >
                Sign up
              </Link>{" "}
              or
              <Link
                className={styles.authLink}
                onClick={() => keycloak.login()}
              >
                Login
              </Link>{" "}
              to comment.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscussionPage;
